/******************************************************************************
 *** MbrasPeriodStats ** 조합원 가입기간 통계
 *******************************************************************************/
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import * as Atom from "@/js/common/Atom";
import { exportToXlsx } from "@/js/common/ExcelExport";
import Button from "@/js/common/ui/Button";
import SelectBox from "@/js/common/ui/input/SelectBox";
import TextBox from "@/js/common/ui/input/TextBox";
import GridTable, { fldInfsProps } from "@/js/common/ui/GridTable";
import AdminGridStyle from "@/js/common/ui/style/AdminGridStyle";
import Util from "@/js/common/Util";
import React from "react";
import { useRecoilValue } from "recoil";

const MbrasPeriodStats = () => {
    const userInfo = useRecoilValue(Atom.userInfo);

    const [keyword          , setKeyword          ] = React.useState("");                                                        // 성명 검색어
    const [upOgnzCd         , setUpOgnzCd         ] = React.useState<string>(userInfo.upOgnzCd);                                 // 본부코드
    const [ognzCd           , setOgnzCd           ] = React.useState<string>(userInfo.ognzCd.includes("000") ? "" : userInfo.ognzCd); // 지부코드
    const [joinPeriodCd     , setJoinPeriodCd     ] = React.useState("");                                                        // 가입기간
    const [dataArr          , setDataArr          ] = React.useState<any[]>([]);                                                 // 조합원 목록

    const joinPeriodCodeArray = [
        { code: "", name: "전체" },
        { code: "01", name: "1개월 이상" },
        { code: "03", name: "3개월 이상" },
        { code: "06", name: "6개월 이상" },
        { code: "12", name: "12개월 이상" },
    ];

    const fldInfs: fldInfsProps[] = [
        { type: "seq" ,                           width:  50, headAlign: "center", dataAlign: "center", label: "No."         },
        { type: "text", fldNm: "mbrasId"        , width: 150, headAlign: "center", dataAlign: "center", label: "조합원번호" },
        { type: "text", fldNm: "mbrasNm"        , width: 100, headAlign: "center", dataAlign: "center", label: "성명"       },
        { type: "code", fldNm: "mbrasJoinSttsCd", width: 100, headAlign: "center", dataAlign: "center", label: "가입상태", catCode: "MBRAS_JOIN_STTS_CD"},
        { type: "text", fldNm: "upOgnzNm"       , width: 160, headAlign: "center", dataAlign: "center", label: "본부"       },
        { type: "text", fldNm: "ognzNm"         , width: 160, headAlign: "center", dataAlign: "center", label: "지부"       },
        { type: "text", fldNm: "wtjrNm"         , width: 140, headAlign: "center", dataAlign: "center", label: "관내"       },
        { type: "text", fldNm: "joinPeriodNm"   , width: 140, headAlign: "center", dataAlign: "center", label: "가입기간"   },
    ];

    const fldInfsVoter: fldInfsProps[] = [
        { type: "seq"  ,                         width:  60, headAlign: "center", dataAlign: "center", label: "일련번호"        },
        { type: "text" , fldNm: "ognzNm"       , width: 200, headAlign: "center", dataAlign: "center", label: "소속(국,과,계)"  },
        { type: "text" , fldNm: "mbrasNm"      , width: 100, headAlign: "center", dataAlign: "center", label: "성명"            },
        { type: "code" , fldNm: "gndrCd"       , width:  80, headAlign: "center", dataAlign: "center", label: "성별", catCode: "GNDR_CD" },
        { type: "date" , fldNm: "brdtDate"     , width: 120, headAlign: "center", dataAlign: "center", label: "생년월일"        },
        { type: "phone", fldNm: "telno"        , width: 140, headAlign: "center", dataAlign: "center", label: "핸드폰 번호"      },
        { type: "text" , fldNm: "voteRcptNm"   , width: 100, headAlign: "center", dataAlign: "center", label: "투표용지 수령인"  },
        { type: "text" , fldNm: "finalRcptNm"  , width: 100, headAlign: "center", dataAlign: "center", label: "결선 수령인"      },
        { type: "text" , fldNm: "rmrk"         , width: 100, headAlign: "center", dataAlign: "center", label: "비고"            },
    ];

    const setJoinPeriodName = (list: any[]) => {
        return list.map((item: any) => {
            const years = Number(item.years ?? 0);
            const months = Number(item.months ?? 0);
            const joinPeriodNm = years === 0 ? `${months}개월` : `${years}년 ${months}개월`;

            return {
                ...item,
                joinPeriodNm: joinPeriodNm,
            };
        });
    };

    React.useEffect(() => { // 조합원별 가입기간 통계 조회
        AppContext.showSpinner();

        Axios.dataAccess({
            url       : "system/mbras/mng/select-mbras-join-period-stats.do",
            methodType: "post",
            paramType : "json",
            paramData : {
                upOgnzCd : upOgnzCd === "00" ? "" : upOgnzCd,
                ognzCd   : ognzCd === "00_000" ? "" : ognzCd,
                joinPeriod: joinPeriodCd,
            },
            onSuccessFn: (res: any) => {
                const list = setJoinPeriodName(res.result.list || []);

                setDataArr(list);
                AppContext.hideSpinner();
            },
        });
    }, [upOgnzCd, ognzCd, joinPeriodCd]);

    const filteredData = Util.filterFieldData(dataArr, fldInfs, "mbrasNm", keyword); // 성명 검색 결과

    const initSearch = () => {
        setKeyword("");
        setUpOgnzCd(userInfo.upOgnzCd);
        setOgnzCd(userInfo.ognzCd.includes("000") ? "" : userInfo.ognzCd);
        setJoinPeriodCd("");
    };

    // 선거인명부 엑셀 다운로드
    // 신규: 1개월 이상, 재가입: 6개월 이상, 휴직(동의), 복직(철회→동의): 1개월 이상
    const downloadVoterList = () => {
        setKeyword("");
        AppContext.showSpinner();

        Axios.dataAccess({
            url       : "system/mbras/mng/select-mbras-voter-list.do",
            methodType: "post",
            paramType : "json",
            paramData : {
                upOgnzCd : upOgnzCd === "00" ? "" : upOgnzCd,
                ognzCd   : ognzCd === "00_000" ? "" : ognzCd,
            },
            onSuccessFn: (res: any) => {
                const voterList = setJoinPeriodName(res.result.list || []);

                setDataArr(voterList);

                if (voterList.length === 0) {
                    AppContext.hideSpinner();
                    AppContext.openAlert({
                        title: "데이터 확인",
                        msg  : "선거인명부로 다운로드할 데이터가 없습니다.",
                        icon : "warning",
                    });
                    return;
                }

                const today = new Date();

                const excelDate =
                    `${today.getFullYear()}년 `
                    + `${today.getMonth() + 1}월 `
                    + `${today.getDate()}일`;

                const excelRows = voterList.map((item: any) => ({
                    ...item,
                    voteRcptNm  : "",
                    finalRcptNm : "",
                    rmrk        : "",
                }));

                exportToXlsx({
                    fileName     : "선거인명부",
                    sheetName    : "선거인명부",
                    title        : "선 거 인 명 부",
                    subTitleRight: excelDate,
                    columns      : fldInfsVoter,
                    rows         : excelRows,
                });

                AppContext.hideSpinner();
            },
        });
    };

    const downloadExcel = () => {
        if (filteredData.length === 0) {
            AppContext.openAlert({
                title: "데이터 확인",
                msg  : "엑셀로 다운로드할 데이터가 없습니다.",
                icon : "warning",
            });
            return;
        }

        exportToXlsx({
            fileName : "조합원 가입기간 통계",
            sheetName: "조합원 가입기간 통계",
            title    : "조합원 가입기간 통계",
            columns  : fldInfs,
            rows     : filteredData,
        });
    };

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            <div className="mt5">
                <span className="posR dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">
                    조합원 가입기간 통계
                </span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 조건 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC gap14 flxWrap">
                    {/* 성명 */}
                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">
                            성명
                        </span>
                        <TextBox
                            value={keyword}
                            width={120}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            style={{
                                backgroundColor: "#fff",
                                padding        : "0 10px",
                            }}
                            placeholder="성명 입력"
                            onChangeFunc={(text: string) => setKeyword(text)}
                        />
                    </div>

                    {/* 본부 */}
                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">
                            본부
                        </span>
                        <SelectBox
                            value={upOgnzCd}
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background={userInfo.ognzCd === "00_000" ? "#fff" : "#F6F6F6"}
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            paddingRight={8}
                            disabled={userInfo.ognzCd !== "00_000"}
                            onChangeFunc={(data: string) => {
                                setUpOgnzCd(data);
                                setOgnzCd(data === "00" ? "00_000" : "");
                            }}
                        />
                    </div>

                    {/* 지부 */}
                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">
                            지부
                        </span>
                        <SelectBox
                            value={ognzCd}
                            width={180}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background={userInfo.ognzCd.includes("000") ? "#fff" : "#F6F6F6"}
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getDownOrgCodeArray(upOgnzCd))}
                            placeholder="전체"
                            paddingRight={8}
                            disabled={!userInfo.ognzCd.includes("000")}
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                        />
                    </div>

                    {/* 가입기간 */}
                    <div className="dpFlx aiC">
                        <span className="w100 fm-c fs15 fc333333 ls1 taL">
                            가입기간(개월)
                        </span>
                        <SelectBox
                            value={joinPeriodCd}
                            width={120}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            codeArray={joinPeriodCodeArray}
                            placeholder="전체"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setJoinPeriodCd(data)}
                        />
                    </div>

                    <div className="dpFlx aiC mlA">
                        <Button
                            width={75}
                            height={40}
                            fontFamily="fm-b"
                            fontSize={16}
                            letterSpacing={-1}
                            variant="styInitBtn"
                            borderRadius={6}
                            buttonClass="dpFlx aiC jcC"
                            buttonName="초기화"
                            onClickFunc={initSearch}
                        />
                    </div>
                </div>
            </div>

            <div className="dpFlx aiC jcSb mb10 h44">
                <div className="fm-b fs16 fc007bff ls1 fw700">
                    <span>조합원 목록</span>
                </div>

                <div className="dpFlx aiC gap10">
                    <Button
                        width={130}
                        height={40}
                        fontFamily="fm-b"
                        fontSize={16}
                        letterSpacing={-1}
                        backgroundColor="#17A2B8"
                        backgroundOnColor="#138496"
                        color="#fff"
                        borderRadius={6}
                        buttonClass="dpFlx aiC jcC"
                        buttonName="선거인명부"
                        onClickFunc={downloadVoterList}
                    />

                    <Button
                        width={150}
                        height={40}
                        fontFamily="fm-b"
                        fontSize={16}
                        letterSpacing={-1}
                        variant="styExcelBtn"
                        borderRadius={6}
                        buttonClass="dpFlx aiC jcC"
                        buttonName="Excel 다운로드"
                        onClickFunc={downloadExcel}
                    />
                </div>
            </div>

            <GridTable
                tableInfo={{
                    tableHeadVsb  : true,
                    styleType     : AdminGridStyle,
                    fldInfs       : fldInfs,
                    dataArr       : filteredData,
                    maxRowNums    : [10, 20, 30],
                    theadTrHeight : 40,
                    tbodyTrHeight : 35,
                    paginateClass : "pt10",
                    paginateStyle : {
                        buttonOnColor    : AppContext.boardThemeColor,
                        buttonWidth      : 22,
                        buttonHeight     : 24,
                        buttonMarginLeft : 1,
                        buttonMarginRight: 1,
                        fontSize         : 13,
                        color            : "#666666",
                        letterSpacing    : -0.8,
                        fontFamily       : "Pretendard-Regular",
                        borderRadius     : 3,
                    },
                    footerVsb: true,
                }}
            />
        </div>
    );
};

export default MbrasPeriodStats;
